import { AvailableLineDataFormulaAssumptions, PreviewAssumptionImpactsResponse } from '@futrli-sdk/consolidations';
import { API_PATHS, REQUEST_METHODS, ROUTE_PARAMS, interpolatePathParams } from '@futrli/shared-services';
import { makeRequestWithResponse } from './shared';
import {
  FullConsolidationAssumptionResponse,
  FullConsolidationAssumptionRequest,
  ConsolidationPreviewImpactsRequestBody,
} from '../../forecast/prediction-builder/types';
import { AllowedAssumptionTypes } from '../../forecast/detail/reports/actions/actions';
import { AiAssumption } from '../types';

/**
 * Fetches an assumption which belongs to a consolidated forecast
 *
 * @param workspaceId - ID of the workspace the consolidation belongs to
 * @param consolidationId - ID of the consolidation the forecast belongs to
 * @param forecastId - ID of the forecast the assumption belongs to
 *
 */
export const createConsolidatedAssumption = async (
  workspaceId: string,
  consolidationId: string,
  forecastId: string,
  data: FullConsolidationAssumptionRequest
) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATION_ASSUMPTIONS, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return makeRequestWithResponse<FullConsolidationAssumptionResponse>({
    data,
    url: path,
    method: REQUEST_METHODS.POST
  });
};

/**
 * Fetches a list of all assumptions for a consolidated forecast
 *
 * @param workspaceId - ID of the workspace the consolidation belongs to
 * @param consolidationId - ID of the consolidation the forecast belongs to
 * @param forecastId - ID of the forecast to get the assumptions for
 */
export const readConsolidatedAssumptions = async (workspaceId: string, consolidationId: string, forecastId: string) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATION_ASSUMPTIONS, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return makeRequestWithResponse<FullConsolidationAssumptionResponse[]>({ url: path, method: REQUEST_METHODS.GET });
};

/**
 * Fetches an assumption which belongs to a consolidated forecast
 *
 * @param workspaceId - ID of the workspace the consolidation belongs to
 * @param consolidationId - ID of the consolidation the forecast belongs to
 * @param forecastId - ID of the forecast the assumption belongs to
 * @param assumptionId - ID of the assumption to retrieve
 */
export const readConsolidatedAssumption = async (
  workspaceId: string,
  consolidationId: string,
  forecastId: string,
  assumptionId: string
) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATION_ASSUMPTION_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId,
    [ROUTE_PARAMS.ASSUMPTION_ID]: assumptionId
  });

  return makeRequestWithResponse<FullConsolidationAssumptionResponse>({ url: path, method: REQUEST_METHODS.GET });
};

/**
 * Fetches line data for a specific entity (line data is used for references / the @ menu)
 *
 * @param workspaceId - ID of the workspace the consolidation belongs to
 * @param consolidationId - ID of the consolidation the forecast belongs to
 * @param forecastId - ID of the forecast the entity belongs to
 * @param entityId - ID of the entity to retrieve line data for
 */
export const readEntityLineData = async (
  workspaceId: string,
  consolidationId: string,
  forecastId: string,
  entityId: string
) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATION_ASSUMPTIONS_LINE_DATA, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId,
    [ROUTE_PARAMS.ENTITY_ID]: entityId
  });

  return makeRequestWithResponse<AvailableLineDataFormulaAssumptions>({ url: path, method: REQUEST_METHODS.GET });
};

/**
 * Fetches impact preview data for the given assumption data
 *
 * @param workspaceId - ID of the workspace the consolidation belongs to
 * @param consolidationId - ID of the consolidation the forecast belongs to
 * @param forecastId - ID of the forecast the assumption belongs to
 * @param data - assumption data to get an impact preview for
 */
export const readImpactPreview = async (
  workspaceId: string,
  consolidationId: string,
  forecastId: string,
  data: ConsolidationPreviewImpactsRequestBody
) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATION_ASSUMPTIONS_IMPACT_PREVIEW, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return makeRequestWithResponse<PreviewAssumptionImpactsResponse>({
    data,
    url: path,
    method: REQUEST_METHODS.POST
  });
};

/**
 * Updates a consolidation assumption with the given data
 *
 * @param workspaceId - ID of the workspace the consolidation belongs to
 * @param consolidationId - ID of the consolidation the forecast belongs to
 * @param forecastId - ID of the forecast the assumption belongs to
 * @param assumptionId - ID of the assumption to retrieve
 */
export const updateConsolidatedAssumption = async (
  workspaceId: string,
  consolidationId: string,
  forecastId: string,
  assumptionId: string,
  data: FullConsolidationAssumptionRequest
) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATION_ASSUMPTION_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId,
    [ROUTE_PARAMS.ASSUMPTION_ID]: assumptionId
  });

  return makeRequestWithResponse<FullConsolidationAssumptionResponse>({ data, url: path, method: REQUEST_METHODS.PUT });
};

/**
 * Patches a consolidated forecast assumption
 *
 * @param {Partial<AllowedAssumptionTypes | AiAssumption | { excluded_from_report: boolean }>} data
 * @param workspaceId - ID of the workspace the consolidation belongs to
 * @param consolidationId - ID of the consolidation the forecast belongs to
 * @param forecastId - ID of the forecast the assumption belongs to
 * @param assumptionId - ID of the assumption to retrieve
 */
export const patchConsolidatedAssumption = async (
  data: Partial<AllowedAssumptionTypes | AiAssumption | { excluded_from_report: boolean }>,
  workspaceId: string,
  consolidationId: string,
  forecastId: string,
  assumptionId: string
): Promise<void> => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATION_ASSUMPTION_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ASSUMPTION_ID]: assumptionId
  });

  await makeRequestWithResponse({ data, url: path, method: REQUEST_METHODS.PATCH });
};

/**
 * Deletes a consolidation assumption
 *
 * @param workspaceId - ID of the workspace the consolidation belongs to
 * @param consolidationId - ID of the consolidation the forecast belongs to
 * @param forecastId - ID of the forecast the assumption belongs to
 * @param assumptionId - ID of the assumption to retrieve
 */
export const deleteConsolidatedAssumption = async (
  workspaceId: string,
  consolidationId: string,
  forecastId: string,
  assumptionId: string
) => {
  const path = interpolatePathParams(API_PATHS.CONSOLIDATION_ASSUMPTION_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: consolidationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId,
    [ROUTE_PARAMS.ASSUMPTION_ID]: assumptionId
  });

  return makeRequestWithResponse<void>({
    url: path,
    method: REQUEST_METHODS.DELETE
  });
};
